import { Component, OnInit, TemplateRef } from '@angular/core';
import { AppComponentName, LastSensorReading, Message, MessageActionType, MessageType, Sensor, SensorGroup, SensorGroupInfo, UserGroup } from '@models';
import { AppMessageService } from '@services/message.service';
import { SensorService } from '@services/sensor.service';
import { SensorGroupService } from '@services/sensorgroup.service';
import { UserService } from '@services/user.service';
import { AppComponent } from 'app/app.component';
import { ChangeLogNotesParams, ChangeLogNotesComponent } from 'app/components/messages/changelog-notes/changelognotes.components';
import { SensorReadingsService } from 'app/components/sensor-readings/sensor-readings.service';
import { Authorization } from 'app/shared/classes/authorization';
import { CommonShared } from 'app/shared/classes/common';
import moment from 'moment';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';

interface DateRange {
  startDate: moment.Moment;
  endDate: moment.Moment;
}

export interface QuickCreateSensorgroupParams {
  sensorsDisplayed: Sensor[];
  selectedUserGroup: UserGroup;
  userGroups: UserGroup[];
}

@Component({
  standalone: false,
  selector: 'quick-create-sensorgroup',
  templateUrl: './quick-create-sensorgroup.component.html',
  styleUrls: ['./quick-create-sensorgroup.component.scss']
})

export class QuickCreateSensorgroupComponent implements OnInit, QuickCreateSensorgroupParams {

  common = new CommonShared();
  auth = new Authorization();

  modalDragOn: boolean = false;

  sensorsDisplayed: Sensor[] = [];
  selectedUserGroup: UserGroup;
  userGroups: UserGroup[] = [];

  sensorGroups: SensorGroup[] = [];
  sensorGroup: SensorGroup;
  username: string;
  duplicateName = false;
  isReadySensorGroups: boolean = false;
  sensorGroupNameTooltip: string = '';

  sensorGroupUUIDCols = [
    { field: 'checkbox', header: '', width: '10%' },
    { field: 'name', header: 'Name', width: '20%' },
    { field: 'uuid', header: 'UUID', width: '50%' },
    { field: 'type', header: 'Type', width: '20%' },
  ];

  changeLogNotes = '';
  preEditObjectAsJSONString = '';
  logModalRef: BsModalRef;
  associatedUserGroups: UserGroup[] = [];
  modalMode: string;

  constructor(public bsModalRef: BsModalRef, private toastr: ToastrService, private modalService: BsModalService,
              public _sensorService: SensorService, public _userService: UserService, public _appMessageService: AppMessageService,
              public _sensorGroupService: SensorGroupService, public _sensorReadingService: SensorReadingsService) {
  }


  ngOnInit() {
    this._userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results['value'];
        if (this.username != null && this.username !== '') {
          this.startUp();
        }
      },
      err => {
        this.toastr.error('Error unable to read current user\'s info');
      });
  }


  startUp() {
    this.modalMode = "Create";
    this.sensorGroup = this.newSensorGroup();
    this.preEditObjectAsJSONString = JSON.stringify(this.sensorGroup);

    this.getSensorGroups();
  }


  setDragOn(drag: boolean) {
    this.modalDragOn = drag;

    if (drag) {
      setTimeout(() => {
        this.modalDragOn = false;
      },3000);
    }
  }


  newSensorGroup(): SensorGroup {
    var sensorGroup: SensorGroup = {
      id: 0,
      name: '',
      description: '',
      createdDate: moment().local().toDate(),
      modifiedDate: moment().local().toDate(),
      beginDate: moment().local().toDate(),
      endDate: moment().local().add(10, 'years').toDate(),
      isDisabled: false,
      crop: '-',
      plantDate: undefined,
      plantAge: 0,
      userGroupId: (this.selectedUserGroup != undefined) ? this.selectedUserGroup.id : null,
      userGroup: (this.selectedUserGroup != undefined) ? this.selectedUserGroup.name : null,
      userAccess: undefined,
      userName: this.username,
      events: [],
      sensors: this.sensorsDisplayed.filter(x => x.checked),
      isNew: true
    };

    this.auth.mapUserAccessForSensorGroup(sensorGroup, true, this.username, this.userGroups);

    return sensorGroup;
  }


  getSensorGroups(): void {
    this._sensorGroupService.getSensorGroupsWithSensors().subscribe(
      results => {
        if (results != null) {
          this.sensorGroups = results;
          this.sensorGroups.forEach(x => {
            this.auth.mapUserAccessForSensorGroup(x, true, this.username, this.userGroups);
          });

          this.isReadySensorGroups = true;
        } else {
          this.toastr.warning('No Sensor Groups found.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while retrieving current display.');
      });
  }


  updateDisplaySensorGroupInfo(sensorgroupName: string) {
    var usingExistingSensorGroup:  boolean = false;

    this.sensorGroups.forEach(x => {
      if (x.name.toLowerCase() == sensorgroupName.toLowerCase()) {
        this.toastr.success('Note: Existing sensor group selected');
        usingExistingSensorGroup = true;
        this.modalMode = "Update";
        this.sensorGroup = JSON.parse(JSON.stringify(x));
        this.preEditObjectAsJSONString = JSON.stringify(x);

        x.sensors.forEach(y => {
          if (!this.sensorsDisplayed.map(z => z.id).includes(y.id)) {
            this.sensorsDisplayed.push(y);
            y.checked = false;
          }
        });
        return;
      }
    });

    if (!usingExistingSensorGroup) {
      this.modalMode = "Create";
      this.sensorGroup = this.newSensorGroup();
      this.sensorGroup.name = sensorgroupName;
    }

    this.sensorGroupNameTooltip = "";
    if (sensorgroupName.length > 1) {
      var similarSensorGroups = this.sensorGroups.filter(x => x.name.toLowerCase().includes(sensorgroupName.toLowerCase()));
      this.sensorGroupNameTooltip = similarSensorGroups.map(x => x.name).join('\n');
    }
  }


  duplicateNamecheck(sensorgroupName: string): boolean {
    this.duplicateName = false;
    this.sensorGroups.forEach(s => {
      if (s.name.toUpperCase() == sensorgroupName.toUpperCase() && s.id != this.sensorGroup.id){
        this.toastr.error('Sensor Group name is already in use');
        this.duplicateName = true;
        return this.duplicateName;
      }
    });
    return this.duplicateName;
  }


  createOrUpdateSensorGroup() {
    if (this.duplicateNamecheck(this.sensorGroup.name)) {
      return;
    }

    if (this.sensorGroup.name == '') {
      this.toastr.error('Please include a group name');
      return;
    }

    this.bsModalRef.hide();
    this.sensorGroup.sensors = this.sensorsDisplayed.filter(s => s.checked == true);

    this.common.updateUserInfoForSensorGroup(this.sensorGroup, this.selectedUserGroup, this.username);

    this.auth.mapUserAccessForSensorGroup(this.sensorGroup, true, this.username, this.userGroups);
    const sensorGroupInfo = this.common.mapSensorGroup2SensorGroupInfo(this.sensorGroup);

    if (this.sensorGroup.id < 1) {
      sensorGroupInfo.endDate = undefined;
      this.onCreate(this.sensorGroup, sensorGroupInfo);
    }
    else {
      sensorGroupInfo.beginDate = sensorGroupInfo.modifiedDate,
      sensorGroupInfo.endDate = moment.utc().toDate(),
      this.onEdit(this.sensorGroup, sensorGroupInfo);
    }
  }


  onCreate(sensorGroup: SensorGroup, sensorGroupInfo: SensorGroupInfo) {
    this._sensorGroupService.createSensorGroup(sensorGroupInfo).subscribe(
      results => {
        if (results != null) {
          this.createMessageData(MessageActionType.New, sensorGroup, '');
          this.toastr.success('Success');
        } else {
          this.toastr.warning('Error with creating new sensor group. Reload page.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while adding sensor group. Reload page.');
      });
  }


  onEdit(sensorGroup: SensorGroup, sensorGroupInfo: SensorGroupInfo) {
    this._sensorGroupService.updateSensorGroup(sensorGroupInfo, this.username).subscribe(
      results => {
        if (results != null) {
          this.createMessageData(MessageActionType.Edited, sensorGroup, this.common.getEditChanges4SensorGroups(sensorGroup, this.preEditObjectAsJSONString));
          this.toastr.success('Sensor Group Updated');
        } else {
          this.toastr.warning('Error saving edits. Reload page.');
        }
        return;
      },
      err => {
        this.toastr.error('Error occurred while updating sensor groups. Reload page.');
      });
  }


  createMessageData(msgActionType: MessageActionType, sensorGroup: SensorGroup, msgNote: string) {
    const message: Message = {
      id: 0,
      messageType: MessageType.Audit,
      appComponentName: AppComponentName.SensorGroups,
      actionType: msgActionType,
      appComponentItem: (sensorGroup != undefined) ? sensorGroup.name : '',
      details: msgNote,
      userNotes: this.changeLogNotes,
      count: 1,
      acknowledged: false,
      userName: this.username,
      userGroupId: (sensorGroup != undefined) ? sensorGroup.userGroupId : null,
    };

    this._appMessageService.createAppMessage(message).subscribe(
      results => {
        if (results != null) {
          //this.toastr.success('Success');
        } else {
          //this.toastr.warning('Not found.');
        }
        return;
      },
      err => {
        //this.toastr.error('Error occurred while saving change log.');
      });
  }


  sensorSelectionChanged(sensor: Sensor) {
  }


  toggleCheckedSensorsDisplayed() {
    if (this.sensorsDisplayed == undefined || this.sensorsDisplayed.length < 1) {
      return;
    }
    var newState = !this.sensorsDisplayed[0].checked;
    this.sensorsDisplayed.forEach(sensor => {
      sensor.checked = newState;
    });
  }


  addNotes2ChangeLog() {
    this.changeLogNotes = '';

    const params: ChangeLogNotesParams = {
      changeLogNotes: this.changeLogNotes,
      userGroupId: 0,
      category: ''
    };

    var modalRef = this.modalService.show(ChangeLogNotesComponent, { animated: false, backdrop: false, ignoreBackdropClick: true, class: 'modal-above-confirmdialog', initialState: params });
    modalRef.content.onChangeLogNotes.subscribe((value) => {
      this.changeLogNotes = value;
    });
  }
 



}
